import { useToast } from 'primevue/usetoast';
import type {
  AdminGenerateTranscriptInput,
  CreateTranscriptTypeInput,
  SubmitTranscriptRequestInput,
  UpdateTranscriptTypeInput,
} from '@/types/composition-functions';
import {
  GetTranscriptRequestDocument,
  GetTranscriptRequestsDocument,
  GetTranscriptTypesDocument,
  useAddTranscriptRequestNoteMutation,
  useAdminGenerateTranscriptMutation,
  useApproveTranscriptRequestMutation,
  useConfirmTranscriptPaymentMutation,
  useCreateTranscriptTypeMutation,
  useDeleteTranscriptTypeMutation,
  useLinkTranscriptApplicantToStudentMutation,
  useMarkTranscriptDispatchedMutation,
  useRegenerateTranscriptDataMutation,
  useRejectTranscriptRequestMutation,
  useSubmitPublicTranscriptRequestMutation,
  useSubmitTranscriptRequestMutation,
  useUpdateTranscriptCoverLetterMutation,
  useUpdateTranscriptTypeMutation,
} from '@/types/vue-apollo-helpers';
import { toastAndRethrow } from '@/utils/mutationErrorToast';

const refetchOnRequest = (id: string) => [
  { query: GetTranscriptRequestsDocument },
  { query: GetTranscriptRequestDocument, variables: { id } },
];

export const useTranscriptMutations = () => {
  const toast = useToast();

  // ── Transcript Types — Admin CRUD ────────────────────────────
  const { mutate: createType, loading: creatingType } = useCreateTranscriptTypeMutation({
    refetchQueries: [{ query: GetTranscriptTypesDocument }],
  });
  const { mutate: updateType, loading: updatingType } = useUpdateTranscriptTypeMutation({
    refetchQueries: [{ query: GetTranscriptTypesDocument }],
  });
  const { mutate: deleteType, loading: deletingType } = useDeleteTranscriptTypeMutation({
    refetchQueries: [{ query: GetTranscriptTypesDocument }],
  });

  // ── Request Workflow — Admin ─────────────────────────────────
  const { mutate: approveMutate, loading: approving } = useApproveTranscriptRequestMutation();
  const { mutate: rejectMutate, loading: rejecting } = useRejectTranscriptRequestMutation();
  const { mutate: regenerateMutate, loading: regenerating } = useRegenerateTranscriptDataMutation();
  const { mutate: adminGenerateMutate, loading: adminGenerating } = useAdminGenerateTranscriptMutation();
  const { mutate: addNoteMutate, loading: addingNote } = useAddTranscriptRequestNoteMutation();
  const { mutate: linkStudentMutate, loading: linkingStudent } = useLinkTranscriptApplicantToStudentMutation();
  const { mutate: markDispatchedMutate, loading: markingDispatched } = useMarkTranscriptDispatchedMutation();
  const { mutate: coverLetterMutate, loading: updatingCoverLetter } = useUpdateTranscriptCoverLetterMutation();

  // ── Student-facing ───────────────────────────────────────────
  const { mutate: submitRequestMutate, loading: submittingRequest } = useSubmitTranscriptRequestMutation();
  const { mutate: confirmPaymentMutate, loading: confirmingPayment } = useConfirmTranscriptPaymentMutation();

  // ── Public ───────────────────────────────────────────────────
  const { mutate: submitPublicRequestMutate, loading: submittingPublicRequest } = useSubmitPublicTranscriptRequestMutation();

  // ── Wrapped helpers: admin actions toast the outcome either way. Errors are
  // rethrown so forms can still map field messages (useFormErrors). The student,
  // public and admin-generate screens toast their own failures, so those
  // helpers only toast success.
  const failed = (summary: string) => toastAndRethrow(toast, summary);

  const createTranscriptType = async(input: CreateTranscriptTypeInput) => {
    const res = await createType({ input }).catch(failed('Couldn\'t create transcript type'));
    toast.add({ severity: 'success', summary: 'Created', detail: 'Transcript type created.', life: 3000 });
    return res?.data?.createTranscriptType ?? null;
  };

  const updateTranscriptType = async(input: UpdateTranscriptTypeInput) => {
    const res = await updateType({ input }).catch(failed('Couldn\'t save transcript type'));
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Transcript type updated.', life: 3000 });
    return res?.data?.updateTranscriptType ?? null;
  };

  const deleteTranscriptType = async(id: string) => {
    await deleteType({ id }).catch(failed('Couldn\'t delete transcript type'));
    toast.add({ severity: 'success', summary: 'Deleted', detail: 'Transcript type deleted.', life: 3000 });
  };

  const approveRequest = async(id: string) => {
    const res = await approveMutate({ id }, { refetchQueries: refetchOnRequest(id) }).catch(failed('Couldn\'t approve request'));
    toast.add({ severity: 'success', summary: 'Approved', detail: 'Transcript request approved.', life: 3000 });
    return res?.data?.approveTranscriptRequest ?? null;
  };

  const rejectRequest = async(id: string, reason: string) => {
    const res = await rejectMutate({ id, reason }, { refetchQueries: refetchOnRequest(id) }).catch(failed('Couldn\'t reject request'));
    toast.add({ severity: 'warn', summary: 'Rejected', detail: 'Transcript request rejected.', life: 3000 });
    return res?.data?.rejectTranscriptRequest ?? null;
  };

  const regenerateData = async(transcript_request_id: string) => {
    const res = await regenerateMutate(
      { transcript_request_id },
      { refetchQueries: refetchOnRequest(transcript_request_id) },
    ).catch(failed('Couldn\'t regenerate transcript data'));
    toast.add({ severity: 'success', summary: 'Regenerated', detail: 'Transcript data regenerated.', life: 3000 });
    return res?.data?.regenerateTranscriptData ?? null;
  };

  const adminGenerateTranscript = async(input: AdminGenerateTranscriptInput) => {
    const res = await adminGenerateMutate({ input });
    toast.add({ severity: 'success', summary: 'Generated', detail: 'Transcript generated.', life: 3000 });
    return res?.data?.adminGenerateTranscript ?? null;
  };

  const addRequestNote = async(
    transcript_request_id: string,
    content: string,
    is_visible_to_applicant = false,
  ) => {
    const res = await addNoteMutate(
      { transcript_request_id, content, is_visible_to_applicant },
      { refetchQueries: refetchOnRequest(transcript_request_id) },
    ).catch(failed('Couldn\'t add note'));
    toast.add({ severity: 'success', summary: 'Note added', life: 2000 });
    return res?.data?.addTranscriptRequestNote ?? null;
  };

  const linkApplicantToStudent = async(transcript_request_id: string, student_id: string) => {
    const res = await linkStudentMutate(
      { transcript_request_id, student_id },
      { refetchQueries: refetchOnRequest(transcript_request_id) },
    ).catch(failed('Couldn\'t link student'));
    toast.add({ severity: 'success', summary: 'Linked', detail: 'Student linked to request.', life: 3000 });
    return res?.data?.linkTranscriptApplicantToStudent ?? null;
  };

  const markRequestDispatched = async(transcript_request_id: string) => {
    const res = await markDispatchedMutate(
      { transcript_request_id },
      { refetchQueries: refetchOnRequest(transcript_request_id) },
    ).catch(failed('Couldn\'t mark as dispatched'));
    toast.add({ severity: 'success', summary: 'Dispatched', detail: 'Marked as dispatched.', life: 3000 });
    return res?.data?.markTranscriptDispatched ?? null;
  };

  const saveCoverLetter = async(
    generated_transcript_id: string,
    cover_letter: string,
    transcript_request_id?: string,
  ) => {
    const res = await coverLetterMutate(
      { generated_transcript_id, cover_letter },
      transcript_request_id ? { refetchQueries: refetchOnRequest(transcript_request_id) } : undefined,
    ).catch(failed('Couldn\'t save cover letter'));
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Cover letter updated.', life: 3000 });
    return res?.data?.updateTranscriptCoverLetter ?? null;
  };

  const submitTranscriptRequest = async(input: SubmitTranscriptRequestInput) => {
    const res = await submitRequestMutate({ input });
    toast.add({ severity: 'success', summary: 'Submitted', detail: 'Transcript request submitted.', life: 3000 });
    return res?.data?.submitTranscriptRequest ?? null;
  };

  const confirmTranscriptPayment = async(transcript_request_id: string, payment_reference: string) => {
    const res = await confirmPaymentMutate({ transcript_request_id, payment_reference });
    toast.add({ severity: 'success', summary: 'Confirmed', detail: 'Payment confirmed.', life: 3000 });
    return res?.data?.confirmTranscriptPayment ?? null;
  };

  const submitPublicTranscriptRequest = async(
    variables: Parameters<typeof submitPublicRequestMutate>[0],
  ) => {
    const res = await submitPublicRequestMutate(variables);
    toast.add({ severity: 'success', summary: 'Submitted', detail: 'Transcript request submitted.', life: 3000 });
    return res?.data?.submitPublicTranscriptRequest ?? null;
  };

  return {
    // Type CRUD
    createTranscriptType, creatingType,
    updateTranscriptType, updatingType,
    deleteTranscriptType, deletingType,
    // Admin actions
    approveRequest, approving,
    rejectRequest, rejecting,
    regenerateData, regenerating,
    adminGenerateTranscript, adminGenerating,
    addRequestNote, addingNote,
    linkApplicantToStudent, linkingStudent,
    markRequestDispatched, markingDispatched,
    saveCoverLetter, updatingCoverLetter,
    // Student-facing
    submitTranscriptRequest, submittingRequest,
    confirmTranscriptPayment, confirmingPayment,
    // Public
    submitPublicTranscriptRequest, submittingPublicRequest,
  };
};
